/**
 * shipDamage.ts
 * Ship-vs-hazard collision resolution for the World 2 space scene.
 *
 * Two responsibilities:
 *  1. `tickShipIframes(dt)` — drain the ship's post-hit invulnerability
 *     clock by one fixed step.
 *  2. `resolveShipHazardCollisions()` — AABB-test the ship against every
 *     active hazard pool (mines, boss turret bolts, boss missiles,
 *     fighter-drone lasers, bomber cluster bombs, bomb shrapnel,
 *     boss body, asteroids, enemies) and apply damage on overlap.
 *
 * Hazard-consumption rules per pool:
 *  - Mines / boss projectiles / boss missiles / asteroids / enemies:
 *    consumed on contact (`.dead = true`) regardless of whether damage
 *    actually landed. A player in iframes still "explodes" the
 *    projectile or mine that touched them — otherwise iframes would
 *    expire mid-overlap and the player would eat a second hit from the
 *    same hazard. Enemies also clear `.active` so `spaceEnemySystem`
 *    reaps them on the next tick.
 *  - Boss body: NEVER consumed. The boss is a solid object; bumping into
 *    it costs HP (subject to iframes) but the boss continues. Skipped
 *    while in `DEFEATED` state so the death animation isn't a damage
 *    source.
 *
 * `spaceState.gameOver` flips the tick the ship's HP reaches zero.
 * `stepSpace` reads this flag and skips its damage pass on subsequent
 * frames so the dead-ship couple of frames before the React layer
 * freezes the canvas can't keep mutating state.
 *
 * NO React imports, NO Skia imports, NO async.
 *
 * Phase 4 — World 2: Starfield Drift.
 *
 * @module shipDamage
 */

import { spaceState } from './spaceState';
import { triggerSpaceShake } from './cameraShake';
import type { AABB } from '../types/entity';
import { isDestructible } from '../../constants/asteroidConstants';
import {
  SHIP_DAMAGE_PER_MINE,
  SHIP_DAMAGE_PER_BOSS_PROJECTILE,
  SHIP_DAMAGE_PER_BOSS_MISSILE,
  SHIP_DAMAGE_PER_BOSS_BODY,
  SHIP_DAMAGE_PER_ASTEROID,
  SHIP_DAMAGE_PER_ENEMY,
  SHIP_DAMAGE_PER_ENEMY_LASER,
  SHIP_DAMAGE_PER_BOMB,
  SHIP_DAMAGE_PER_SHRAPNEL,
  SHIP_BOUNCE_VELOCITY_PX_S,
  SPACE_SHAKE_MAGNITUDE_SHIP_HIT_PX,
  SPACE_SHAKE_DURATION_SHIP_HIT_S,
} from '../../constants/spaceConstants';

/**
 * AABB overlap test. Inclusive on the low edge, exclusive on the high
 * edge — same convention as the rest of the engine's collision passes.
 */
function overlaps(a: AABB, b: AABB): boolean {
  return (
    a.x < b.x + b.width &&
    a.x + a.width > b.x &&
    a.y < b.y + b.height &&
    a.y + a.height > b.y
  );
}

/**
 * Decrement the ship's invulnerability clock by one fixed step.
 * No-op when no ship is active or the clock is already 0.
 *
 * @param dt - Fixed timestep in seconds.
 */
export function tickShipIframes(dt: number): void {
  const ship = spaceState.ship;
  if (ship === null) return;
  if (ship.iframesRemaining <= 0) return;
  ship.iframesRemaining = Math.max(0, ship.iframesRemaining - dt);
}

/**
 * Resolve ship-vs-hazard collisions for the current tick.
 *
 * Bails immediately when:
 *  - no ship is active,
 *  - the ship is already dead,
 *  - `spaceState.gameOver` is already true (one death only per session).
 *
 * Iterates pools in a fixed order so the first damaging hit consistently
 * dictates which hazard pool's `.dead` flag gets set on a multi-overlap
 * frame. Order is mines → boss projectiles → boss missiles → fighter
 * lasers → cluster bombs → bomb shrapnel → boss body → asteroids →
 * enemies: slow / deliberate hazards first, fast incidental hazards
 * last. Enemies sit at the tail because their `.active` reset cascades
 * into `spaceEnemySystem`'s reap pass — keeping them last means
 * asteroid contact in the same tick is logged as the asteroid's kill,
 * not the enemy fleet's.
 *
 * DECISION: We iterate the full hazard list even after the ship's iframes
 * arm on the first hit so every overlapping hazard is consumed in the
 * same tick. Otherwise a tight cluster (mine swarm, projectile spread)
 * would leave secondary hazards floating inside the now-invulnerable
 * ship, ready to instant-hit when iframes expire.
 */
export function resolveShipHazardCollisions(): void {
  if (spaceState.gameOver) return;
  const ship = spaceState.ship;
  if (ship === null || ship.dead) return;

  const shipBox = ship.getHitbox();

  // ── Mines ─────────────────────────────────────────────────────────────────
  // IDLE and ACTIVE mines both damage on contact. The state machine
  // governs homing intensity, not whether the warhead is armed.
  for (const mine of spaceState.mines) {
    if (mine.dead) continue;
    if (!overlaps(shipBox, mine.getHitbox())) continue;
    if (ship.takeDamage(SHIP_DAMAGE_PER_MINE)) {
      triggerSpaceShake(SPACE_SHAKE_MAGNITUDE_SHIP_HIT_PX, SPACE_SHAKE_DURATION_SHIP_HIT_S);
    }
    mine.dead = true;
    if (ship.dead) {
      spaceState.gameOver = true;
      return;
    }
  }

  // ── Boss turret bolts ─────────────────────────────────────────────────────
  for (const bolt of spaceState.bossProjectiles) {
    if (bolt.dead) continue;
    if (!overlaps(shipBox, bolt.getHitbox())) continue;
    if (ship.takeDamage(SHIP_DAMAGE_PER_BOSS_PROJECTILE)) {
      triggerSpaceShake(SPACE_SHAKE_MAGNITUDE_SHIP_HIT_PX, SPACE_SHAKE_DURATION_SHIP_HIT_S);
    }
    bolt.dead = true;
    if (ship.dead) {
      spaceState.gameOver = true;
      return;
    }
  }

  // ── Boss missiles ─────────────────────────────────────────────────────────
  for (const missile of spaceState.bossMissiles) {
    if (missile.dead) continue;
    if (!overlaps(shipBox, missile.getHitbox())) continue;
    if (ship.takeDamage(SHIP_DAMAGE_PER_BOSS_MISSILE)) {
      triggerSpaceShake(SPACE_SHAKE_MAGNITUDE_SHIP_HIT_PX, SPACE_SHAKE_DURATION_SHIP_HIT_S);
    }
    missile.dead = true;
    if (ship.dead) {
      spaceState.gameOver = true;
      return;
    }
  }
  // ── Fighter-drone laser bolts ───────────────────────────────────────────────
  // Consumed on contact the same way boss bolts are — even during iframes,
  // so a bolt cluster doesn't leave secondaries floating inside the ship
  // ready to instant-hit on iframe expiry.
  for (const bolt of spaceState.fighterLasers) {
    if (bolt.dead) continue;
    if (!overlaps(shipBox, bolt.getHitbox())) continue;
    if (ship.takeDamage(SHIP_DAMAGE_PER_ENEMY_LASER)) {
      triggerSpaceShake(SPACE_SHAKE_MAGNITUDE_SHIP_HIT_PX, SPACE_SHAKE_DURATION_SHIP_HIT_S);
    }
    bolt.dead = true;
    if (ship.dead) {
      spaceState.gameOver = true;
      return;
    }
  }

  // ── Bomber cluster bombs (parent payload, pre-detonation) ─────────────
  // Consumed on contact: a bomb that hits the ship explodes against it
  // rather than continuing through and detonating a tick later. The
  // shrapnel fan still spawns from where the bomb stopped because
  // `bomb.dead = true` here flips through the same reap path in
  // `updateBomberOrdnance` that runs the detonation handoff — only
  // when `bomb.detonated` is true. DECISION: We do NOT flip
  // `bomb.detonated = true` here. A bomb consumed by ship contact is
  // a single-point hit, not a cluster spray; flipping it would
  // double-punish a player who happened to fly into the bomb early.
  for (const bomb of spaceState.clusterBombs) {
    if (bomb.dead) continue;
    if (!overlaps(shipBox, bomb.getHitbox())) continue;
    if (ship.takeDamage(SHIP_DAMAGE_PER_BOMB)) {
      triggerSpaceShake(SPACE_SHAKE_MAGNITUDE_SHIP_HIT_PX, SPACE_SHAKE_DURATION_SHIP_HIT_S);
    }
    // DECISION: Flip BOTH `dead` and `active`. The reap pass in
    // `updateBomberOrdnance` calls `bomb.update()` BEFORE checking
    // `dead`; `ClusterBomb.update` early-returns on `!active`. Leaving
    // `active = true` here lets the fuse tick down and set
    // `detonated = true` on the next call — spawning shrapnel from a
    // contact-consumed bomb, which contradicts the single-point-hit
    // contract above.
    bomb.dead = true;
    bomb.active = false;
    if (ship.dead) {
      spaceState.gameOver = true;
      return;
    }
  }
  // ── Bomb shrapnel (post-detonation fragments) ─────────────────────
  // Consumed on contact (mirror of laser/bomb): a fragment that hits
  // the ship is spent. Iframes still gate the HP cost, but the
  // fragment still dies so the ship doesn't keep re-eating it tick
  // after tick while the iframe window plays out.
  for (const piece of spaceState.bombShrapnel) {
    if (piece.dead) continue;
    if (!overlaps(shipBox, piece.getHitbox())) continue;
    if (ship.takeDamage(SHIP_DAMAGE_PER_SHRAPNEL)) {
      triggerSpaceShake(SPACE_SHAKE_MAGNITUDE_SHIP_HIT_PX, SPACE_SHAKE_DURATION_SHIP_HIT_S);
    }
    // Mirror the bomb consume above: kill `active` so the TTL countdown
    // doesn't keep running on a consumed piece before reap.
    piece.dead = true;
    piece.active = false;
    if (ship.dead) {
      spaceState.gameOver = true;
      return;
    }
  }

  // ── Boss body (ram / overlap) ─────────────────────────────────────────────
  // DECISION: Boss is NEVER consumed by contact — bumping into a solid
  // object doesn't destroy it. Skipped during DEFEATED so the death
  // sequence isn't a damage source. The takeDamage call is gated by
  // iframes internally so repeated overlaps across ticks don't bypass
  // the post-hit grace period.
  const boss = spaceState.boss;
  if (boss !== null && !boss.dead && boss.state !== 'DEFEATED') {
    if (overlaps(shipBox, boss.getHitbox())) {
      if (ship.takeDamage(SHIP_DAMAGE_PER_BOSS_BODY)) {
        triggerSpaceShake(SPACE_SHAKE_MAGNITUDE_SHIP_HIT_PX, SPACE_SHAKE_DURATION_SHIP_HIT_S);
      }
      if (ship.dead) {
        spaceState.gameOver = true;
        return;
      }
    }
  }

  // ── Asteroids ─────────────────────────────────────────────────────────────
  // DECISION: Destructible rocks (`sm` / `md`) consume on contact — the
  // smash is the reward for taking the hit. Indestructible rocks
  // (`lg` / `xl`) bounce the ship by overwriting `vx` with
  // `SHIP_BOUNCE_VELOCITY_PX_S` instead of dying, so a sustained
  // overlap can't grant infinite damage (iframes still gate the HP)
  // and the ship visibly recoils. Using an absolute overwrite (not
  // an additive impulse) keeps the bounce bounded across the
  // multi-tick overlap window.
  for (const rock of spaceState.asteroids) {
    if (rock.dead) continue;
    if (!overlaps(shipBox, rock.getHitbox())) continue;
    if (ship.takeDamage(SHIP_DAMAGE_PER_ASTEROID)) {
      triggerSpaceShake(SPACE_SHAKE_MAGNITUDE_SHIP_HIT_PX, SPACE_SHAKE_DURATION_SHIP_HIT_S);
    }
    if (isDestructible(rock.size)) {
      rock.dead = true;
    } else {
      ship.vx = SHIP_BOUNCE_VELOCITY_PX_S;
    }
    if (ship.dead) {
      spaceState.gameOver = true;
      return;
    }
  }

  // ── Enemy bodies (fighter / bomber / shield satellite ram) ────────────────
  // DECISION: Skip enemies that are not `.active` even if `.dead` is false —
  // `markSpawned()` sets both true together and `reset()` clears both, but
  // an enemy mid-recycle should never count as a ram threat. Consume on
  // contact: clear both flags so `spaceEnemySystem` reaps the slot next
  // tick alongside laser-killed enemies.
  for (const enemy of spaceState.enemies) {
    if (enemy.dead || !enemy.active) continue;
    if (!overlaps(shipBox, enemy.getHitbox())) continue;
    if (ship.takeDamage(SHIP_DAMAGE_PER_ENEMY)) {
      triggerSpaceShake(SPACE_SHAKE_MAGNITUDE_SHIP_HIT_PX, SPACE_SHAKE_DURATION_SHIP_HIT_S);
    }
    enemy.dead = true;
    enemy.active = false;
    if (ship.dead) {
      spaceState.gameOver = true;
      return;
    }
  }
}
